import Grid from '../Geodata/grid';
import FVector from '@data/geometry/FVector.js';
import { SensorPoint } from '../Simulations/SensorPoint.js';
import { toRadians } from 'Utils/UtilityFunctions';

export class OctTree {
    // The root node
    root;

    constructor(boundary: Boundary, _nItems: number, opts?: { rotationAngle?: number; rotationOrigin?: FVector }) {
        // 1. Rotate the OctTree boundary from World space to Grid space inside Boundary.
        // This allows to make all OctTrees computation in an axis-aligned space.
        const rootBoundary = boundary.withRotation(opts);

        // Create root:
        this.root = new Node(1, rootBoundary, (item) => this.getItemCenter(item));
        Node.MAX_ITEMS = 10; // nItems/1000
    }

    insert(item: SensorPoint) {
        const inserted = this.root.insert(item);

        if (!inserted) console.warn('The following item has not been adeed', item);
    }

    /**
     * Search for all the sensorpoints along the given direction
     *
     * @param {Ray} ray should be an object containing start and direction
     * @returns a list of sensor points
     */
    searchByRay(ray: { start: any; direction: FVector }, sensorPoint: SensorPoint) {
        const results = new Set<SensorPoint>();
        this.root.searchByRay(ray, results, sensorPoint);
        return Array.from(results);
    }

    searchFirstItemByRay(ray: { start: any; direction: FVector }, sensorPoint: SensorPoint, predicate: (item: SensorPoint) => boolean) {
        const isIntersected = this.root.searchFirstItemByRay(ray, sensorPoint, predicate);

        return isIntersected;
    }

    searchByRadius(center: FVector, radius: number) {
        const results = new Set<SensorPoint>();
        this.root.searchByRadius(center, radius, results);
        return Array.from(results);
    }

    searchClosest(point: FVector) {
        return this.root.searchClosest(point);
    }

    private getItemCenter(item: SensorPoint) {
        return item.getCenter();
    }
}

class Node {
    static MAX_ITEMS = 10; // Maximum number of items before subdivision

    level: number;
    boundary: Boundary;
    private readonly getItemCenter: (item: SensorPoint) => FVector;

    isLeaf = true;

    children: Node[] = []; // ORDER: SW - SE- NW - NE

    items: SensorPoint[] = [];

    constructor(level: number, boundary: Boundary, getItemCenter: (item: SensorPoint) => FVector) {
        this.level = level;
        this.boundary = boundary;
        this.getItemCenter = getItemCenter;
    }

    insert(item: SensorPoint) {
        const itemCenter = this.getItemCenter(item);
        const itemBoundary = this.boundary.createFromCenter(itemCenter, Grid.gridRes, Grid.gridRes, Grid.gridRes);

        if (!this.boundary.intersectsBoundary(itemBoundary)) {
            return false;
        }

        if (this.isLeaf) {
            if (this.items.length < Node.MAX_ITEMS) {
                this.items.push(item);
                return true;
            } else {
                this.subdivide();
            }
        }

        // Insert the item into the appropriate child
        let inserted = false;
        for (const child of this.children) {
            if (child.insert(item)) {
                inserted = true;
            }
        }
        if (!inserted) {
            throw new Error('Item could not be assigned to any child.');
        }
        return inserted;
    }

    subdivide() {
        this.isLeaf = false;
        const { x, y, z, xLength, yLength, zLength } = this.boundary;
        const halfXLength = xLength / 2;
        const halfYLength = yLength / 2;
        const halfZLength = zLength / 2;

        // Create eight children
        this.children = [
            new Node(this.level + 1, this.boundary.createChild(x, y, z, halfXLength, halfYLength, halfZLength), this.getItemCenter),
            new Node(
                this.level + 1,
                this.boundary.createChild(x + halfXLength, y, z, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
            new Node(
                this.level + 1,
                this.boundary.createChild(x, y + halfYLength, z, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
            new Node(
                this.level + 1,
                this.boundary.createChild(x + halfXLength, y + halfYLength, z, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
            new Node(
                this.level + 1,
                this.boundary.createChild(x, y, z + halfZLength, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
            new Node(
                this.level + 1,
                this.boundary.createChild(x + halfXLength, y, z + halfZLength, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
            new Node(
                this.level + 1,
                this.boundary.createChild(x, y + halfYLength, z + halfZLength, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
            new Node(
                this.level + 1,
                this.boundary.createChild(x + halfXLength, y + halfYLength, z + halfZLength, halfXLength, halfYLength, halfZLength),
                this.getItemCenter
            ),
        ];

        // Move the items to the appropriate children
        for (const item of this.items) {
            let assigned = false;
            for (const child of this.children) {
                if (child.insert(item)) {
                    assigned = true;
                    // break;
                }
            }
            if (!assigned) {
                throw new Error('Item could not be assigned to any child.');
            }
        }

        // Clear the items in this node
        this.items = [];
    }

    searchByRay(ray: { start: any; direction: FVector }, results: Set<SensorPoint>, sensorPoint: SensorPoint) {
        if (!this.boundary.intersectsRay(ray)) {
            return;
        }

        for (const item of this.items) {
            const itemCenter = this.getItemCenter(item);
            const itemBoundary = this.boundary.createFromCenter(itemCenter, Grid.gridRes, Grid.gridRes, Grid.gridRes);
            if (itemBoundary.intersectsRay(ray)) {
                results.add(item);
            }
        }

        if (!this.isLeaf) {
            for (const child of this.children) {
                child.searchByRay(ray, results, sensorPoint);
            }
        }
    }

    searchFirstItemByRay(ray: { start: any; direction: FVector }, sensorPoint: SensorPoint, predicate: (item: SensorPoint) => boolean) {
        // Return if no Node-Ray intersections are found
        if (!this.boundary.intersectsRay(ray)) {
            return false;
        }

        // Check intersection with every sensor point bounding box
        for (const item of this.items) {
            // If the item we are going to intersect is itSelf, go ahead
            if (item.getID() == sensorPoint.getID()) continue;

            const itemCenter = this.getItemCenter(item);
            const itemBoundary = this.boundary.createFromCenter(itemCenter, Grid.gridRes, Grid.gridRes, Grid.gridRes);
            if (itemBoundary.intersectsRay(ray)) {
                if (!predicate || predicate(item)) return true;
            }
        }

        // Recursively check child nodes if this is not a leaf
        if (!this.isLeaf) {
            for (const child of this.children) {
                if (child.searchFirstItemByRay(ray, sensorPoint, predicate)) {
                    return true; // Stop further recursion if an active item is found
                }
            }
        }

        return false; // Continue recursion if no active item is found
    }

    searchByRadius(center: FVector, radius: number, results: Set<SensorPoint>) {
        if (!this.boundary.intersectsSphere(center, radius)) {
            return;
        }

        for (const item of this.items) {
            const itemCenter = this.getItemCenter(item);
            const itemBoundary = this.boundary.createFromCenter(itemCenter, Grid.gridRes, Grid.gridRes, Grid.gridRes);

            if (this.boundary.intersectsBoundary(itemBoundary)) {
                results.add(item);
            }
        }

        if (!this.isLeaf) {
            for (const child of this.children) {
                child.searchByRadius(center, radius, results);
            }
        }
    }

    searchClosest(point: FVector) {
        let closest = null;
        let closestDistance = Infinity;
        const pq = new MinPriorityQueue();

        pq.enqueue(this, 0);

        while (!pq.isEmpty()) {
            const next = pq.dequeue();
            if (!next) break;
            const { element: node, priority: nodeDist } = next;

            if (nodeDist >= closestDistance) break;

            for (const item of node.items) {
                const distance = FVector.dist(point, this.getItemCenter(item));
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closest = item;
                }
            }

            if (!node.isLeaf) {
                for (const child of node.children) {
                    const childDist = child.boundary.distanceToPoint(point);
                    if (childDist < closestDistance) {
                        pq.enqueue(child, childDist);
                    }
                }
            }
        }

        return closest;
    }

    static explore(node: Node) {
        if (!node) return;
        console.log(`\n Level = ${node.level}. `);
        console.log(
            `  Boundary: [x:${node.boundary.x},y:${node.boundary.y},z:${node.boundary.z}] - [width:${node.boundary.width},height:${node.boundary.height},depth:${node.boundary.depth}]`
        );
        console.log(`   isLeaft: ${node.isLeaf}`);

        if (node.isLeaf) {
            for (let item of node.items) {
                console.log(item);
            }
            console.log(' Leaf node finished.');
            return;
        }
        for (let child of node.children) {
            Node.explore(child);
        }
    }
}

export class Boundary {
    x: number;
    y: number;
    z: number;
    xLength: number;
    yLength: number;
    zLength: number;
    private readonly rotation?: ReturnType<typeof Boundary.createRotation>;
    constructor(
        x: number,
        y: number,
        z: number,
        xLength: number,
        yLength: number,
        zLength: number,
        rotation?: ReturnType<typeof Boundary.createRotation>
    ) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.xLength = xLength;
        this.yLength = yLength;
        this.zLength = zLength;
        this.rotation = rotation;
    }

    withRotation(opts?: { rotationAngle?: number; rotationOrigin?: FVector }) {
        const rotation = Boundary.createRotation(opts);
        if (!rotation.hasRotation()) {
            return new Boundary(this.x, this.y, this.z, this.xLength, this.yLength, this.zLength);
        }

        const rotated = rotation.rotateBoundary(this);
        return new Boundary(rotated.x, rotated.y, rotated.z, rotated.xLength, rotated.yLength, rotated.zLength, rotation);
    }

    hasRotation() {
        return !!this.rotation?.hasRotation();
    }

    createChild(x: number, y: number, z: number, xLength: number, yLength: number, zLength: number) {
        return new Boundary(x, y, z, xLength, yLength, zLength, this.rotation);
    }

    createFromCenter(center: FVector, xLength: number, yLength: number, zLength: number) {
        const localCenter = this.toLocalPoint(center);
        return new Boundary(
            localCenter.x - xLength / 2,
            localCenter.y - yLength / 2,
            localCenter.z - zLength / 2,
            xLength,
            yLength,
            zLength,
            this.rotation
        );
    }

    static createRotation(opts?: { rotationAngle?: number; rotationOrigin?: FVector }) {
        const angle = opts?.rotationAngle ? toRadians(opts.rotationAngle) : 0;
        const origin = opts?.rotationOrigin
            ? new FVector(opts.rotationOrigin.x, opts.rotationOrigin.y, opts.rotationOrigin.z)
            : new FVector();
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);
        const hasRotation = () => sinAngle !== 0 || cosAngle !== 1;

        const rotatePoint = (point: { x: number; y: number; z: number }) => {
            if (!hasRotation()) return point as FVector;
            const dx = point.x - origin.x;
            const dy = point.y - origin.y;
            return new FVector(origin.x + dx * cosAngle - dy * sinAngle, origin.y + dx * sinAngle + dy * cosAngle, point.z);
        };

        const rotateDirection = (direction: FVector) => {
            if (!hasRotation()) return direction;
            return new FVector(
                direction.x * cosAngle - direction.y * sinAngle,
                direction.x * sinAngle + direction.y * cosAngle,
                direction.z
            );
        };

        const rotateRay = (ray: { start: any; direction: FVector }) => {
            if (!hasRotation()) return ray;
            return {
                start: rotatePoint(ray.start),
                direction: rotateDirection(ray.direction),
            };
        };

        const rotateBoundary = (boundary: Boundary) => {
            if (!hasRotation()) return boundary;
            const corners = [
                new FVector(boundary.x, boundary.y, boundary.z),
                new FVector(boundary.x + boundary.xLength, boundary.y, boundary.z),
                new FVector(boundary.x, boundary.y + boundary.yLength, boundary.z),
                new FVector(boundary.x + boundary.xLength, boundary.y + boundary.yLength, boundary.z),
            ];

            const rotated = corners.map((corner) => rotatePoint(corner));
            const minX = Math.min(...rotated.map((corner) => corner.x));
            const maxX = Math.max(...rotated.map((corner) => corner.x));
            const minY = Math.min(...rotated.map((corner) => corner.y));
            const maxY = Math.max(...rotated.map((corner) => corner.y));
            const minZ = boundary.z;
            const maxZ = boundary.z + boundary.zLength;

            return new Boundary(minX, minY, minZ, maxX - minX, maxY - minY, maxZ - minZ);
        };

        return { hasRotation, rotatePoint, rotateDirection, rotateRay, rotateBoundary };
    }

    get width() {
        return this.xLength;
    }

    get height() {
        return this.yLength;
    }

    get depth() {
        return this.zLength;
    }

    private toLocalPoint(point: FVector) {
        return this.rotation ? this.rotation.rotatePoint(point) : point;
    }

    private toLocalRay(ray: { start: FVector; direction: FVector }) {
        return this.rotation ? this.rotation.rotateRay(ray) : ray;
    }

    private toLocalBoundary(boundary: Boundary) {
        if (!this.rotation) return boundary;
        if (boundary.rotation === this.rotation) return boundary;
        return this.rotation.rotateBoundary(boundary);
    }

    contains(item: FVector) {
        const localItem = this.toLocalPoint(item);
        return (
            localItem.x >= this.x &&
            localItem.x < this.x + this.xLength && // Exclusive upper bound
            localItem.y >= this.y &&
            localItem.y < this.y + this.yLength && // Exclusive upper bound
            localItem.z >= this.z &&
            localItem.z < this.z + this.zLength // Exclusive upper bound
        );
    }

    intersectsBoundary(boundary: Boundary) {
        const localBoundary = this.toLocalBoundary(boundary);
        return (
            this.x < localBoundary.x + localBoundary.xLength &&
            this.x + this.xLength > localBoundary.x &&
            this.y < localBoundary.y + localBoundary.yLength &&
            this.y + this.yLength > localBoundary.y &&
            this.z < localBoundary.z + localBoundary.zLength &&
            this.z + this.zLength > localBoundary.z
        );
    }

    intersectsRay(ray: { start: FVector; direction: FVector }) {
        const { start, direction } = this.toLocalRay(ray);

        const invDirX = direction.x !== 0 ? 1 / direction.x : Infinity;
        const invDirY = direction.y !== 0 ? 1 / direction.y : Infinity;
        const invDirZ = direction.z !== 0 ? 1 / direction.z : Infinity;

        let tmin = (this.x - start.x) * invDirX;
        let tmax = (this.x + this.xLength - start.x) * invDirX;
        if (tmin > tmax) [tmin, tmax] = [tmax, tmin];

        let tymin = (this.y - start.y) * invDirY;
        let tymax = (this.y + this.yLength - start.y) * invDirY;
        if (tymin > tymax) [tymin, tymax] = [tymax, tymin];

        if (tmin >= tymax || tymin >= tmax) return false;
        if (tymin > tmin) tmin = tymin;
        if (tymax < tmax) tmax = tymax;

        let tzmin = (this.z - start.z) * invDirZ;
        let tzmax = (this.z + this.zLength - start.z) * invDirZ;
        if (tzmin > tzmax) [tzmin, tzmax] = [tzmax, tzmin];

        if (tmin >= tzmax || tzmin >= tmax) return false;
        if (tzmin > tmin) tmin = tzmin;
        if (tzmax < tmax) tmax = tzmax;

        // Enforce exclusive entry: require that entry point is strictly inside the box
        return tmax > 0 && tmin < tmax;
    }

    intersectsSphere(center: FVector, radius: number) {
        const localCenter = this.toLocalPoint(center);
        const dx = Math.max(this.x - localCenter.x, 0, localCenter.x - (this.x + this.xLength));
        const dy = Math.max(this.y - localCenter.y, 0, localCenter.y - (this.y + this.yLength));
        const dz = Math.max(this.z - localCenter.z, 0, localCenter.z - (this.z + this.zLength));
        return dx * dx + dy * dy + dz * dz <= radius * radius;
    }

    distanceToPoint(point: FVector) {
        const localPoint = this.toLocalPoint(point);
        const dx = Math.max(this.x - localPoint.x, 0, localPoint.x - (this.x + this.xLength));
        const dy = Math.max(this.y - localPoint.y, 0, localPoint.y - (this.y + this.yLength));
        const dz = Math.max(this.z - localPoint.z, 0, localPoint.z - (this.z + this.zLength));
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
}
class MinPriorityQueue {
    items: { element: any; priority: number }[];
    constructor() {
        this.items = [];
    }

    enqueue(element: any, priority: number) {
        this.items.push({ element, priority });
        this.items.sort((a, b) => a.priority - b.priority);
    }

    dequeue(): { element: any; priority: number } | undefined {
        return this.items.shift();
    }

    isEmpty() {
        return this.items.length === 0;
    }
}

/*// Example usage
// This benchmark could be used in the future as unit test
// My last benchmark performed with 1M of sensors 13s for the init and 55 ms for the search
function benchmark() {
    Grid.gridRes = 5;
    const numItems = 100000;

    const boundary = new Boundary(0, 0, 0, 1000, 1000, 1000);
    const octTree = new OctTree(boundary, numItems);

    // Insert 100,000 random items
    const items = [];
    for (let i = 0; i < numItems; i++) {
        const item = new SensorPoint(new FVector(Math.random() * 1000, Math.random() * 1000, Math.random() * 1000));
        items.push(item);
    }

    console.time('Insert 100,000 items');
    for (const item of items) {
        octTree.insert(item);
    }
    console.timeEnd('Insert 100,000 items');

    // Search closest point to a random point
    const target = new FVector(Math.random() * 1000, Math.random() * 1000, Math.random() * 1000);

    console.time('Search closest');
    const closest = octTree.searchClosest(target);
    console.timeEnd('Search closest');

    console.log('Closest point:', closest);

    console.time('Search By ray');
    const ray = { start: new FVector(), direction: new FVector(1, 2, 4) };
    const intersectedSpheres = octTree.searchByRay(ray);
    console.log(intersectedSpheres);
    console.timeEnd('Search By ray');

    console.log('by ray', closest);

    console.time('Search By radius');
    const searchCenter = { x: 30, y: 30, z: 30 };
    const searchRadius = 100;
    const spheresWithinRadius = octTree.searchByRadius(searchCenter, searchRadius);
    console.timeEnd('Search By radius');

    console.log('by radius', spheresWithinRadius);
}

// benchmark();*/
